import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/auth';
import {
  CreditCard,
  Plus,
  Trash2,
  Check,
  DollarSign,
  Clock,
  Loader2,
  AlertCircle,
  Star,
} from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';

interface PaymentMethod {
  id: string;
  provider: string;
  accountId: string;
  accountName: string | null;
  currency: string;
  isDefault: boolean;
  isVerified: boolean;
  createdAt: string;
}

interface PayoutRequest {
  id: string;
  amount: string | number;
  currency: string;
  status: string;
  requestedAt: string;
  completedAt: string | null;
  failureReason: string | null;
  paymentMethod: {
    provider: string;
    accountId: string;
  };
  transaction: {
    fee: string | number;
    netAmount: string | number;
    providerStatus: string | null;
  } | null;
}

interface AffiliateBalance {
  totalEarnings: number;
  totalPaid: number;
  pendingBalance: number;
  availableBalance: number;
}

const providerNames: Record<string, string> = {
  paypal: 'PayPal',
  wise: 'Wise',
  stripe: 'Stripe',
  binance: 'Binance Pay',
};

export default function PaymentMethods() {
  const { accessToken } = useAuthStore();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [requests, setRequests] = useState<PayoutRequest[]>([]);
  const [balance, setBalance] = useState<AffiliateBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    provider: 'paypal',
    accountId: '',
    accountName: '',
    isDefault: false,
  });

  const [payoutData, setPayoutData] = useState({
    paymentMethodId: '',
    amount: '',
  });

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [methodsRes, requestsRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/payment-providers/methods`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_BASE}/api/v1/payment-providers/requests`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      const [methodsData, requestsData] = await Promise.all([
        methodsRes.json(),
        requestsRes.json(),
      ]);

      if (methodsData.success) setMethods(methodsData.data);
      if (requestsData.success) setRequests(requestsData.data);

      // TODO: Fetch balance from dedicated API endpoint
      // Balance calculation should come from a separate API call
      setBalance({
        totalEarnings: 0,
        totalPaid: 0,
        pendingBalance: 0,
        availableBalance: 0,
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMethod = async () => {
    if (!formData.accountId) return;

    setProcessing(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/methods`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (data.success) {
        setAddModalOpen(false);
        setFormData({ provider: 'paypal', accountId: '', accountName: '', isDefault: false });
        fetchData();
      } else {
        alert(data.error || 'Failed to add payment method');
      }
    } catch (error) {
      console.error('Failed to add method:', error);
      alert('Failed to add payment method');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteMethod = async (id: string) => {
    if (!confirm('Are you sure you want to delete this payment method?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/methods/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const data = await response.json();
      if (data.success) {
        fetchData();
      } else {
        alert(data.error || 'Failed to delete payment method');
      }
    } catch (error) {
      console.error('Failed to delete method:', error);
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/methods/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ isDefault: true }),
      });

      const data = await response.json();
      if (data.success) {
        fetchData();
      } else {
        alert(data.error || 'Failed to set default');
      }
    } catch (error) {
      console.error('Failed to set default:', error);
    }
  };

  const handleRequestPayout = async () => {
    if (!payoutData.paymentMethodId || !payoutData.amount) return;

    setProcessing(true);
    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          paymentMethodId: payoutData.paymentMethodId,
          amount: parseFloat(payoutData.amount),
        }),
      });

      const data = await response.json();
      if (data.success) {
        setRequestModalOpen(false);
        setPayoutData({ paymentMethodId: '', amount: '' });
        fetchData();
      } else {
        alert(data.error || 'Failed to request payout');
      }
    } catch (error) {
      console.error('Failed to request payout:', error);
      alert('Failed to request payout');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelRequest = async (id: string) => {
    if (!confirm('Are you sure you want to cancel this payout request?')) return;

    try {
      const response = await fetch(`${API_BASE}/api/v1/payment-providers/requests/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const data = await response.json();
      if (data.success) {
        fetchData();
      } else {
        alert(data.error || 'Failed to cancel request');
      }
    } catch (error) {
      console.error('Failed to cancel request:', error);
    }
  };

  const formatAmount = (amount: string | number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(typeof amount === 'string' ? parseFloat(amount) : amount);
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'warning' | 'info' | 'success' | 'error' | 'default'> = {
      pending: 'warning',
      approved: 'info',
      processing: 'info',
      completed: 'success',
      failed: 'error',
      cancelled: 'default',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">Payment Methods</h1>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Manage your payment methods and request payouts.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2">
          <Button variant="secondary" onClick={() => setAddModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Method
          </Button>
          {methods.length > 0 && balance && balance.availableBalance >= 50 && (
            <Button variant="primary" onClick={() => setRequestModalOpen(true)}>
              <DollarSign className="w-4 h-4 mr-2" />
              Request Payout
            </Button>
          )}
        </div>
      </div>

      {/* Balance Card */}
      {balance && (
        <Card className="bg-gradient-to-r from-primary-500/10 to-accent-500/10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <span className="text-sm text-[var(--text-muted)]">Total Earnings</span>
              <p className="text-2xl font-bold text-[var(--text-primary)]">
                {formatAmount(balance.totalEarnings)}
              </p>
            </div>
            <div>
              <span className="text-sm text-[var(--text-muted)]">Total Paid</span>
              <p className="text-2xl font-bold text-green-400">{formatAmount(balance.totalPaid)}</p>
            </div>
            <div>
              <span className="text-sm text-[var(--text-muted)]">Pending</span>
              <p className="text-2xl font-bold text-yellow-400">
                {formatAmount(balance.pendingBalance)}
              </p>
            </div>
            <div>
              <span className="text-sm text-[var(--text-muted)]">Available</span>
              <p className="text-2xl font-bold text-primary-400">
                {formatAmount(balance.availableBalance)}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Payment Methods */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Your Payment Methods</h2>
        {methods.length === 0 ? (
          <Card className="text-center py-8">
            <CreditCard className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-4" />
            <p className="text-[var(--text-secondary)]">No payment methods added yet.</p>
            <Button variant="primary" size="sm" className="mt-4" onClick={() => setAddModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Payment Method
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {methods.map((method) => (
              <Card key={method.id} className="relative">
                {method.isDefault && (
                  <div className="absolute top-4 right-4">
                    <Badge variant="purple">
                      <Star className="w-3 h-3 mr-1" />
                      Default
                    </Badge>
                  </div>
                )}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[var(--bg-tertiary)] rounded-xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-[var(--text-primary)]">
                      {providerNames[method.provider] || method.provider}
                    </h3>
                    <p className="text-sm text-[var(--text-muted)]">{method.accountId}</p>
                    {method.accountName && (
                      <p className="text-sm text-[var(--text-secondary)]">{method.accountName}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      {method.isVerified ? (
                        <Badge variant="success">
                          <Check className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="warning">Unverified</Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  {!method.isDefault && (
                    <Button variant="secondary" size="sm" onClick={() => handleSetDefault(method.id)}>
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteMethod(method.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Payout Requests */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-4">Payout History</h2>
        {requests.length === 0 ? (
          <Card className="text-center py-8">
            <Clock className="w-12 h-12 mx-auto text-[var(--text-muted)] mb-4" />
            <p className="text-[var(--text-secondary)]">No payout requests yet.</p>
          </Card>
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="table-dark min-w-full">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Requested</th>
                    <th>Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-color)]">
                  {requests.map((request) => (
                    <tr key={request.id}>
                      <td className="font-semibold text-[var(--text-primary)]">
                        {formatAmount(request.amount)}
                        {request.transaction && (
                          <div className="text-xs text-[var(--text-muted)]">
                            Fee: {formatAmount(request.transaction.fee)} | Net:{' '}
                            {formatAmount(request.transaction.netAmount)}
                          </div>
                        )}
                      </td>
                      <td>
                        <Badge variant="info">{request.paymentMethod.provider}</Badge>
                        <div className="text-xs text-[var(--text-muted)] mt-1">
                          {request.paymentMethod.accountId}
                        </div>
                      </td>
                      <td>{getStatusBadge(request.status)}</td>
                      <td className="text-[var(--text-secondary)]">
                        {formatDate(request.requestedAt)}
                      </td>
                      <td className="text-[var(--text-secondary)]">
                        {formatDate(request.completedAt)}
                      </td>
                      <td>
                        {request.status === 'pending' && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleCancelRequest(request.id)}
                          >
                            Cancel
                          </Button>
                        )}
                        {request.failureReason && (
                          <span className="text-xs text-red-400" title={request.failureReason}>
                            <AlertCircle className="w-4 h-4" />
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Add Payment Method Modal */}
      {addModalOpen && (
        <div className="modal-overlay" onClick={() => setAddModalOpen(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[var(--border-color)]">
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">Add Payment Method</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="label">Provider</label>
                <select
                  className="input"
                  value={formData.provider}
                  onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                >
                  <option value="paypal">PayPal</option>
                  <option value="wise">Wise (TransferWise)</option>
                  <option value="binance">Binance Pay</option>
                </select>
              </div>
              <div>
                <label className="label">
                  {formData.provider === 'paypal'
                    ? 'PayPal Email'
                    : formData.provider === 'binance'
                    ? 'Binance Pay ID'
                    : 'Account ID'}
                </label>
                <input
                  type={formData.provider === 'paypal' ? 'email' : 'text'}
                  className="input"
                  placeholder={
                    formData.provider === 'paypal'
                      ? 'your@email.com'
                      : formData.provider === 'binance'
                      ? 'Enter Binance Pay ID'
                      : 'Enter account ID'
                  }
                  value={formData.accountId}
                  onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Account Name (Optional)</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Display name for this account"
                  value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isDefault"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="isDefault" className="text-sm text-[var(--text-secondary)]">
                  Set as default payment method
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[var(--border-color)] flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setAddModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAddMethod}
                disabled={!formData.accountId || processing}
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Method'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Request Payout Modal */}
      {requestModalOpen && (
        <div className="modal-overlay" onClick={() => setRequestModalOpen(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[var(--border-color)]">
              <h2 className="text-xl font-semibold text-[var(--text-primary)]">Request Payout</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-4 bg-[var(--bg-tertiary)] rounded-lg">
                <span className="text-sm text-[var(--text-muted)]">Available Balance</span>
                <p className="text-2xl font-bold text-primary-400">
                  {balance ? formatAmount(balance.availableBalance) : '$0.00'}
                </p>
              </div>
              <div>
                <label className="label">Payment Method</label>
                <select
                  className="input"
                  value={payoutData.paymentMethodId}
                  onChange={(e) => setPayoutData({ ...payoutData, paymentMethodId: e.target.value })}
                >
                  <option value="">Select payment method</option>
                  {methods.map((method) => (
                    <option key={method.id} value={method.id}>
                      {providerNames[method.provider]} - {method.accountId}
                      {method.isDefault ? ' (Default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Amount ($)</label>
                <input
                  type="number"
                  className="input"
                  placeholder="Enter amount"
                  min="50"
                  max={balance?.availableBalance || 0}
                  value={payoutData.amount}
                  onChange={(e) => setPayoutData({ ...payoutData, amount: e.target.value })}
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">Minimum payout: $50</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[var(--border-color)] flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setRequestModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleRequestPayout}
                disabled={
                  !payoutData.paymentMethodId ||
                  !payoutData.amount ||
                  parseFloat(payoutData.amount) < 50 ||
                  processing
                }
              >
                {processing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Request Payout'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
